/**
 * slotAvailability — the single source of truth for "is this ticket bookable on
 * the visit date, and how much capacity is left?".
 *
 * Main entry tickets are slot-based (one or more time slots per day), so their
 * catalog availability is derived from the schedule, not a stock counter. This
 * ports the customer online FE's `slotAvailability.js` predicates so the two
 * apps agree on what counts as bookable, then adds a catalog-level summary
 * (remaining capacity + why a ticket is unavailable) for the ProductCard.
 *
 * A slot is bookable when it is:
 *   1. open — status `OPEN` or `EVENT_OVERRIDE`,
 *   2. has capacity — `availableCount > 0`, or unknown (`null`/absent),
 *   3. not already ended on the visit date (today-only past detection).
 *
 * Times are 'HH:mm' local strings; `visitDate` is 'YYYY-MM-DD' (or undefined →
 * anchor to today). `nowMs` is injectable for testing; defaults to `Date.now()`.
 */
import type { Slot } from '@/types/product.slot';

interface SlotOpts {
  visitDate?: string;
  nowMs?: number;
}

function toHourMinute(hhmm?: string): [number, number] | null {
  const parts = String(hhmm ?? '').split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  return Number.isNaN(h) || Number.isNaN(m) ? null : [h, m];
}

/** Whether a slot's end time has already passed on the visit date. */
export function isSlotPast(localEnd?: string, { visitDate, nowMs = Date.now() }: SlotOpts = {}): boolean {
  const hm = toHourMinute(localEnd);
  if (!hm) return false;
  const [hh, mm] = hm;
  let y: number | undefined;
  let mo = 0;
  let d = 1;
  if (visitDate) {
    const [yy, mm0, dd] = String(visitDate).split('-').map(Number);
    if (
      yy !== undefined &&
      mm0 !== undefined &&
      dd !== undefined &&
      !Number.isNaN(yy) &&
      !Number.isNaN(mm0) &&
      !Number.isNaN(dd)
    ) {
      y = yy;
      mo = mm0 - 1;
      d = dd;
    }
  }
  if (y === undefined) {
    const now = new Date(nowMs);
    y = now.getFullYear();
    mo = now.getMonth();
    d = now.getDate();
  }
  return new Date(y, mo, d, hh, mm, 0, 0).getTime() < nowMs;
}

/** Whether a single slot can still be booked (open + has capacity + not ended). */
export function isBookableSlot(slot: Slot | null | undefined, opts: SlotOpts = {}): boolean {
  if (!slot) return false;
  if (slot.status !== 'OPEN' && slot.status !== 'EVENT_OVERRIDE') return false;
  if (slot.availableCount != null && slot.availableCount <= 0) return false;
  if (isSlotPast(slot.localEnd, opts)) return false;
  return true;
}

/** The bookable subset of a slot list (empty array when none / not an array). */
export function getBookableSlots(slots: Slot[] | undefined, opts: SlotOpts = {}): Slot[] {
  return Array.isArray(slots) ? slots.filter((s) => isBookableSlot(s, opts)) : [];
}

export type UnavailableReason = 'expired' | 'full' | 'none';

export interface AvailabilitySummary {
  state: 'available' | 'unavailable';
  /** Present only when `state === 'unavailable'`. */
  reason?: UnavailableReason;
  /**
   * Remaining capacity across bookable slots; `null` when unknown/unlimited (the
   * backend didn't expose a count). Always a number (0) when unavailable.
   */
  remaining: number | null;
}

/**
 * Roll a slot list up into a catalog-card verdict. When bookable slots exist the
 * ticket is available and `remaining` sums their capacity; otherwise it is
 * unavailable and `reason` says why so the card can pick the right watermark:
 *   - 'expired' — every slot's time has passed on the visit date,
 *   - 'full'    — every slot is sold out / closed,
 *   - 'none'    — no schedule for the date (or a mix that leaves nothing bookable).
 */
export function summarizeAvailability(slots: Slot[] | undefined, opts: SlotOpts = {}): AvailabilitySummary {
  const list = Array.isArray(slots) ? slots : [];
  const bookable = getBookableSlots(list, opts);

  if (bookable.length > 0) {
    let remaining: number | null = null;
    for (const s of bookable) {
      if (typeof s.availableCount === 'number') remaining = (remaining ?? 0) + s.availableCount;
    }
    return { state: 'available', remaining };
  }

  if (list.length === 0) {
    return { state: 'unavailable', reason: 'none', remaining: 0 };
  }

  // Has slots but none bookable — classify why for a precise label.
  const states = list.map((slot) => {
    if (isSlotPast(slot.localEnd, opts)) return 'past';
    const bookableStatus = slot.status === 'OPEN' || slot.status === 'EVENT_OVERRIDE';
    const soldOut = slot.availableCount != null && slot.availableCount <= 0;
    return !bookableStatus || soldOut ? 'soldout' : 'open';
  });

  const reason: UnavailableReason = states.every((s) => s === 'past')
    ? 'expired'
    : states.every((s) => s === 'soldout')
      ? 'full'
      : 'none';

  return { state: 'unavailable', reason, remaining: 0 };
}
